import { CircularProgress } from '@mui/material';
import UPButton from 'components/button';
import ErrorBar from 'components/error-bar';
import UPInput from 'components/input';
import UPSidePanel, { UPSidePanelSection } from 'components/side-panel';
import { MinorNightWorkRule } from 'constants/MinorNightWorkRule';
import { useTranslation } from 'react-i18next';
import {
  CONTRACT_COMPLIANCE__AGE_FROM,
  CONTRACT_COMPLIANCE__AGE_TO,
  CONTRACT_COMPLIANCE__DEFAULT_INTERVAL_HINT,
  CONTRACT_COMPLIANCE__EDIT_DEFAULT_VALUE,
  CONTRACT_COMPLIANCE__TAKES_EFFECT_HINT,
  CONTRACT_COMPLIANCE__YEARS,
  GENERAL__CLOSE,
  GENERAL__RETRY,
  GENERAL__SAVE_BUTTON,
} from 'translations/constants';
import { UseRuleDefaultPanelResult } from '../hooks/useRuleDefaultPanel';
import IntervalFields from '../IntervalFields';
import JustificationFields from '../JustificationFields';
import { HintText, PanelErrorRow, PanelFields } from '../styles';
import { hasAgeBand, MAX_MINOR_AGE_YEARS, RULE_TITLE_KEYS } from './constants';
import { NightWorkDefaultDraft } from './utils';

interface NightWorkDefaultPanelProps {
  panel: UseRuleDefaultPanelResult<MinorNightWorkRule, NightWorkDefaultDraft>;
}

const TEST_ID = 'minor-night-work-default';

const AGE_TEST_IDS: Record<'minAge' | 'maxAge', string> = {
  minAge: 'min-age',
  maxAge: 'max-age',
};

const NightWorkDefaultPanel = ({ panel }: NightWorkDefaultPanelProps): JSX.Element => {
  const [t] = useTranslation();
  const { rule, status, draft, fieldErrors, isSaving, loadErrorMessage, saveErrorMessage } = panel;
  const isBusy = isSaving || panel.justification.isUploading;

  const renderAge = (field: 'minAge' | 'maxAge', labelKey: string) => {
    const errorKey = fieldErrors[field];
    return (
      <UPInput
        type="number"
        emptyAsNull
        min={0}
        max={MAX_MINOR_AGE_YEARS}
        step={1}
        unit={t(CONTRACT_COMPLIANCE__YEARS)}
        label={t(labelKey)}
        placeholder=""
        value={draft[field]}
        onChange={(value: number | null) => panel.setField(field, value === null ? '' : String(value))}
        error={!!errorKey}
        helperText={errorKey ? t(errorKey, { max: MAX_MINOR_AGE_YEARS }) : ''}
        disabled={isSaving}
        required
        testId={`${TEST_ID}-${AGE_TEST_IDS[field]}`}
      />
    );
  };

  return (
    <UPSidePanel
      open={status !== 'closed'}
      title={rule ? t(RULE_TITLE_KEYS[rule]) : ''}
      subtitle={t(CONTRACT_COMPLIANCE__EDIT_DEFAULT_VALUE)}
      onClose={panel.close}
      testId={`${TEST_ID}-panel`}
      footer={
        <>
          <UPButton
            text={t(GENERAL__SAVE_BUTTON)}
            onClick={panel.requestSave}
            disabled={status !== 'ready' || isBusy || !panel.isComplete}
            testId={`${TEST_ID}-save`}
          />
          <UPButton text={t(GENERAL__CLOSE)} onClick={panel.close} outlined testId={`${TEST_ID}-close`} />
        </>
      }
    >
      {status === 'loading' && <CircularProgress data-testid={`${TEST_ID}-panel-loading`} />}

      {status === 'loadError' && loadErrorMessage && (
        <PanelErrorRow data-testid={`${TEST_ID}-panel-load-error`}>
          <ErrorBar message={loadErrorMessage} />
          <UPButton text={t(GENERAL__RETRY)} onClick={panel.retryLoad} outlined testId={`${TEST_ID}-panel-retry`} />
        </PanelErrorRow>
      )}

      {saveErrorMessage && (
        <PanelErrorRow data-testid={`${TEST_ID}-save-error`}>
          <ErrorBar message={saveErrorMessage} />
        </PanelErrorRow>
      )}

      {status === 'ready' && rule && (
        <UPSidePanelSection title={t(CONTRACT_COMPLIANCE__EDIT_DEFAULT_VALUE)} testId={`${TEST_ID}-details`}>
          <>
            <PanelFields>
              <IntervalFields
                value={{ start: draft.start, end: draft.end }}
                onChange={panel.setField}
                errors={{ start: fieldErrors.start, end: fieldErrors.end }}
                disabled={isSaving}
                required
                testIdPrefix={TEST_ID}
              />
              <HintText>{t(CONTRACT_COMPLIANCE__DEFAULT_INTERVAL_HINT)}</HintText>
              {hasAgeBand(rule) && (
                <>
                  {renderAge('minAge', CONTRACT_COMPLIANCE__AGE_FROM)}
                  {renderAge('maxAge', CONTRACT_COMPLIANCE__AGE_TO)}
                </>
              )}
            </PanelFields>
            <HintText>{t(CONTRACT_COMPLIANCE__TAKES_EFFECT_HINT)}</HintText>
            <JustificationFields justification={panel.justification} disabled={isSaving} testIdPrefix={TEST_ID} />
          </>
        </UPSidePanelSection>
      )}
    </UPSidePanel>
  );
};

export default NightWorkDefaultPanel;
